/**
 * Hook to register user for a Mathlete 101 batch
 * Based on starry-night reference implementation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { MATHLETE_CONFIG } from '@/features/user-notifications/utils/constants';

import { registerForMathleteBatch } from '../api/register-mathlete-batch';
import type { RegisterForMathleteBatchResponse } from '../types';

export interface UseRegisterMathleteBatchResult {
  registerForBatch: (batchId: string) => Promise<void>;
  isRegistering: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  data?: RegisterForMathleteBatchResponse;
}

/**
 * Finds Mathlete course by course ID from the configuration
 */
const findMathleteCourse = (
  courseId: string
): { name: string; courseId: string; variantId: string } | undefined => {
  return MATHLETE_CONFIG.courses.find(
    course => course.courseId === courseId
  ) as { name: string; courseId: string; variantId: string } | undefined;
};

/**
 * Hook to register a user for a Mathlete batch
 *
 * @param courseId - The course ID to register for
 * @returns Registration function and mutation state
 *
 * @example
 * ```tsx
 * const { registerForBatch, isRegistering } = useRegisterMathleteBatch('course-123');
 *
 * const handleRegister = async () => {
 *   await registerForBatch('batch-456');
 * };
 * ```
 */
export const useRegisterMathleteBatch = (
  courseId: string
): UseRegisterMathleteBatchResult => {
  const queryClient = useQueryClient();
  const course = findMathleteCourse(courseId);

  const mutation = useMutation({
    mutationFn: async (batchId: string) => {
      if (!batchId) {
        throw new Error('Batch ID is required for registration');
      }
      return registerForMathleteBatch({
        batch_id: batchId,
        variant_id: course?.variantId,
      });
    },
    mutationKey: [MATHLETE_CONFIG.queryKeys.registration],
    onSuccess: () => {
      // Invalidate user profiles to refresh enrolled courses
      void queryClient.invalidateQueries({ queryKey: ['profiles'] });
      // Invalidate modules to refresh dashboard
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });

  return {
    registerForBatch: mutation.mutateAsync,
    isRegistering: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
  };
};
